<template>
  <div class="min-h-screen bg-[#f8f9fa]">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-[#dadce0]">
      <div class="max-w-full mx-auto px-6">
        <div class="flex justify-between items-center h-16">
          <!-- Logo and Title -->
          <div class="flex items-center space-x-6">
            <img 
              src="/images/logo.png" 
              alt="SAC" 
              class="h-8 w-auto"
              @error="handleImageError"
            />
            <div>
              <h1 class="text-xl font-medium text-[#202124]">SAC Analytics</h1>
              <p class="text-sm text-[#5f6368]">Customer Feedback Insights</p>
            </div>
          </div>

          <!-- User Menu -->
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-3">
              <img 
                v-if="displayUser?.picture" 
                :src="displayUser.picture" 
                :alt="displayUser.name"
                class="h-8 w-8 rounded-full"
              />
              <div v-else class="w-8 h-8 bg-[#1a73e8] rounded-full flex items-center justify-center">
                <span class="text-white text-sm font-medium">{{ displayUser?.name?.charAt(0) || 'A' }}</span>
              </div>
              <div class="text-sm">
                <p class="font-medium text-[#202124]">{{ displayUser?.name || 'Administrator' }}</p>
                <p class="text-[#5f6368]">Administrator</p>
              </div>
            </div>
            <button
              @click="handleSignOut"
              class="text-[#5f6368] hover:text-[#202124] px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Sidebar Navigation -->
    <div class="flex">
      <nav class="bg-white w-64 min-h-[calc(100vh-64px)] border-r border-[#dadce0] shadow-sm">
        <div class="p-4">
          <div class="space-y-1">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                activeTab === tab.id
                  ? 'bg-[#e8f0fe] text-[#1a73e8] border-r-3 border-[#1a73e8]'
                  : 'text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124]'
              ]"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="tab.id === 'overview'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                <path v-else-if="tab.id === 'analytics'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z M20.488 9H15V3.512a9.025 9.025 0 015.488 5.488z"/>
                <path v-else-if="tab.id === 'tracking'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"/>
                <path v-else-if="tab.id === 'email-settings'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z"/>
              </svg>
              <span>{{ tab.name }}</span>
            </button>
          </div>
        </div>
      </nav>

      <!-- Main Content -->
      <main class="flex-1 bg-[#f8f9fa] p-6">
        <!-- Dashboard Overview -->
        <div v-if="activeTab === 'overview'" class="space-y-6">
          <!-- Header -->
          <div class="flex items-center justify-between">
            <h2 class="text-2xl font-medium text-[#202124]">Overview</h2>
            <div class="flex items-center space-x-4">
              <button
                @click="refreshStats"
                class="flex items-center space-x-2 px-3 py-1 text-sm text-[#1a73e8] hover:text-[#1557b0] border border-[#dadce0] rounded-md hover:bg-[#f8f9fa] transition-colors"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <span>Refresh</span>
              </button>
              <select class="border border-[#dadce0] rounded-md px-3 py-1 text-sm bg-white">
                <option>Last 7 days</option>
                <option>Last 30 days</option>
                <option>Last 90 days</option>
              </select>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <!-- Visitors -->
            <div class="bg-white rounded-lg border border-[#dadce0] p-6 hover:shadow-md transition-shadow">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <p class="text-xs font-medium text-[#5f6368] uppercase tracking-wide mb-2">VISITORS</p>
                  <p class="text-3xl font-normal text-[#202124] mb-1">{{ visitorStats.total }}</p>
                  <div class="flex items-center text-sm">
                    <span class="text-[#137333] flex items-center">
                      <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                      </svg>
                      +{{ visitorStats.growth }}%
                    </span>
                    <span class="text-[#5f6368] ml-1">from last period</span>
                  </div>
                </div>
                <div class="w-10 h-10 bg-[#fef7cd] rounded-full flex items-center justify-center">
                  <svg class="h-5 w-5 text-[#ea8600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Total Feedback -->
            <div class="bg-white rounded-lg border border-[#dadce0] p-6 hover:shadow-md transition-shadow">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <p class="text-xs font-medium text-[#5f6368] uppercase tracking-wide mb-2">TOTAL FEEDBACK</p>
                  <p class="text-3xl font-normal text-[#202124] mb-1">{{ stats.total }}</p>
                  <div class="flex items-center text-sm">
                    <span class="text-[#137333] flex items-center">
                      <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                      </svg>
                      +12%
                    </span>
                    <span class="text-[#5f6368] ml-1">from last period</span>
                  </div>
                </div>
                <div class="w-10 h-10 bg-[#e8f0fe] rounded-full flex items-center justify-center">
                  <svg class="h-5 w-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Positive Reviews -->
            <div class="bg-white rounded-lg border border-[#dadce0] p-6 hover:shadow-md transition-shadow">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <p class="text-xs font-medium text-[#5f6368] uppercase tracking-wide mb-2">POSITIVE REVIEWS</p>
                  <p class="text-3xl font-normal text-[#202124] mb-1">{{ stats.positive }}</p>
                  <div class="flex items-center text-sm">
                    <span class="text-[#137333]">{{ positivePercentage }}%</span>
                    <span class="text-[#5f6368] ml-1">of total feedback</span>
                  </div>
                </div>
                <div class="w-10 h-10 bg-[#e6f4ea] rounded-full flex items-center justify-center">
                  <svg class="h-5 w-5 text-[#137333]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"/>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Negative Reviews -->
            <div class="bg-white rounded-lg border border-[#dadce0] p-6 hover:shadow-md transition-shadow">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <p class="text-xs font-medium text-[#5f6368] uppercase tracking-wide mb-2">COMPLAINTS</p>
                  <p class="text-3xl font-normal text-[#202124] mb-1">{{ stats.negative }}</p>
                  <div class="flex items-center text-sm">
                    <span class="text-[#d93025]">{{ negativePercentage }}%</span>
                    <span class="text-[#5f6368] ml-1">of total feedback</span>
                  </div>
                </div>
                <div class="w-10 h-10 bg-[#fce8e6] rounded-full flex items-center justify-center">
                  <svg class="h-5 w-5 text-[#d93025]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018c.163 0 .326.02.485.06L17 4m-7 10v2a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"/>
                  </svg>
                </div>
              </div>
            </div>


          </div>

          <!-- Customer Feedback Table -->
          <div class="bg-white rounded-lg border border-[#dadce0]">
            <div class="px-6 py-4 border-b border-[#dadce0]">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-[#202124]">Customer Feedback</h3>
                <div class="text-sm text-[#5f6368]">
                  {{ recentFeedback.length }} entries
                </div>
              </div>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-[#dadce0]">
                <thead class="bg-[#f8f9fa]">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Customer</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Contact</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Service</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Feedback</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Date</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-[#dadce0]">
                  <tr v-if="recentFeedback.length === 0">
                    <td colspan="7" class="px-6 py-12 text-center text-[#5f6368]">
                      <div class="flex flex-col items-center">
                        <svg class="h-12 w-12 mb-4 text-[#dadce0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                        </svg>
                        <p class="text-sm">No feedback available yet</p>
                        <p class="text-xs text-[#9aa0a6] mt-1">Customer feedback will appear here once submitted</p>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="feedback in recentFeedback" :key="feedback.id" class="hover:bg-[#f8f9fa]">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        feedback.type === 'positive' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      ]">
                        <svg :class="[
                          'w-1.5 h-1.5 mr-1.5',
                          feedback.type === 'positive' ? 'text-green-400' : 'text-red-400'
                        ]" fill="currentColor" viewBox="0 0 8 8">
                          <circle cx="4" cy="4" r="3"/>
                        </svg>
                        {{ feedback.type === 'positive' ? 'Positive' : 'Complaint' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-[#202124]">{{ feedback.customer || 'Anonymous' }}</div>
                      <div class="text-sm text-[#5f6368]">{{ feedback.vehicle || 'N/A' }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-[#202124]">{{ feedback.phone || 'Not provided' }}</div>
                      <div class="text-sm text-[#5f6368]">{{ feedback.email || 'Not provided' }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-[#202124]">{{ feedback.service || 'General' }}</div>
                      <div class="text-sm text-[#5f6368]">{{ feedback.employee || 'N/A' }}</div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="text-sm text-[#202124] max-w-xs truncate">{{ feedback.message }}</div>
                      <div v-if="feedback.rating" class="text-sm text-[#5f6368] mt-1">
                        Rating: {{ feedback.rating }}/5 ⭐
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-[#5f6368]">
                      {{ formatDate(feedback.timestamp) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        @click="deleteFeedback(feedback.id)"
                        class="text-red-600 hover:text-red-900 transition-colors duration-200 p-2 rounded-md hover:bg-red-50"
                        title="Delete feedback"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Analytics Tab -->
        <div v-if="activeTab === 'analytics'" class="space-y-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-medium text-[#202124]">Analytics</h2>
          </div>
          <!-- Date Range Filter -->
          <div class="bg-white rounded-lg border border-[#dadce0] p-4 mb-6">
            <div class="flex items-center gap-4 flex-wrap">
              <label class="text-sm font-medium text-[#202124]">Date Range:</label>
              <select v-model="selectedDateRange" @change="updateChartData" class="border border-[#dadce0] rounded-md px-3 py-2 text-sm">
                <option value="1">Today</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
                <option value="custom">Custom Range</option>
              </select>
              
              <!-- Custom Date Range Inputs -->
              <div v-if="selectedDateRange === 'custom'" class="flex items-center gap-2">
                <input 
                  type="date" 
                  v-model="customStartDate" 
                  @change="updateChartData"
                  class="border border-[#dadce0] rounded-md px-3 py-2 text-sm"
                />
                <span class="text-sm text-[#5f6368]">to</span>
                <input 
                  type="date" 
                  v-model="customEndDate" 
                  @change="updateChartData"
                  class="border border-[#dadce0] rounded-md px-3 py-2 text-sm"
                />
              </div>
              
              <button @click="refreshAnalytics" class="bg-[#1a73e8] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#1557b0] transition-colors">
                Refresh
              </button>
            </div>
          </div>

          <!-- Charts Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Feedback Trends Chart -->
            <div class="bg-white rounded-lg border border-[#dadce0] p-6">
              <h3 class="text-lg font-medium text-[#202124] mb-4">Feedback Trends</h3>
              <div class="h-64">
                <Line
                  v-if="chartData.labels.length > 0"
                  :data="chartData"
                  :options="chartOptions"
                />
                <div v-else class="h-full flex items-center justify-center text-[#5f6368]">
                  No data available for selected period
                </div>
              </div>
            </div>

            <!-- Sentiment Distribution Chart -->
            <div class="bg-white rounded-lg border border-[#dadce0] p-6">
              <h3 class="text-lg font-medium text-[#202124] mb-4">Sentiment Distribution</h3>
              <div class="h-64">
                <Doughnut
                  v-if="pieChartData.labels.length > 0"
                  :data="pieChartData"
                  :options="pieChartOptions"
                />
                <div v-else class="h-full flex items-center justify-center text-[#5f6368]">
                  No data available
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Stats -->
          <div class="bg-white rounded-lg border border-[#dadce0] p-6 mt-6">
            <h3 class="text-lg font-medium text-[#202124] mb-4">Period Summary</h3>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div class="text-center p-4 bg-green-50 rounded-lg">
                <div class="text-2xl font-bold text-green-600">{{ periodStats.positive }}</div>
                <div class="text-sm text-green-600">Positive Reviews</div>
              </div>
              <div class="text-center p-4 bg-red-50 rounded-lg">
                <div class="text-2xl font-bold text-red-600">{{ periodStats.negative }}</div>
                <div class="text-sm text-red-600">Negative Reviews</div>
              </div>
              <div class="text-center p-4 bg-blue-50 rounded-lg">
                <div class="text-2xl font-bold text-blue-600">{{ visitorStats.total || 0 }}</div>
                <div class="text-sm text-blue-600">Total Visitors</div>
              </div>
              <div class="text-center p-4 bg-purple-50 rounded-lg">
                <div class="text-2xl font-bold text-purple-600">{{ Math.round(periodStats.positive / (periodStats.total || 1) * 100) }}%</div>
                <div class="text-sm text-purple-600">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Employee Tracking Tab -->
        <div v-if="activeTab === 'tracking'" class="space-y-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-medium text-[#202124]">Employee Performance Tracking</h2>
            <div class="flex items-center space-x-3 flex-wrap">
              <!-- Period Filter -->
              <select 
                v-model="employeeChartPeriod" 
                @change="updateEmployeeChart"
                class="px-3 py-2 border border-[#dadce0] rounded-md text-sm"
              >
                <option value="1">Today</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              
              <!-- Custom Date Range Inputs for Employee Tracking -->
              <div v-if="employeeChartPeriod === 'custom'" class="flex items-center gap-2">
                <input 
                  type="date" 
                  v-model="employeeCustomStartDate" 
                  @change="updateEmployeeChart"
                  class="border border-[#dadce0] rounded-md px-3 py-2 text-sm"
                />
                <span class="text-sm text-[#5f6368]">to</span>
                <input 
                  type="date" 
                  v-model="employeeCustomEndDate" 
                  @change="updateEmployeeChart"
                  class="border border-[#dadce0] rounded-md px-3 py-2 text-sm"
                />
              </div>
              <button 
                @click="refreshEmployeeData"
                class="bg-[#1a73e8] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#1557b0] transition-colors"
              >
                Refresh Data
              </button>
            </div>
          </div>

          <!-- Employee Performance Chart -->
          <div class="bg-white rounded-lg border border-[#dadce0] p-6">
            <div class="mb-6">
              <h3 class="text-lg font-medium text-[#202124] mb-2">Visit Performance by Employee</h3>
              <p class="text-sm text-[#5f6368]">Track how many customers visit through each employee's link</p>
            </div>
            
            <!-- Employee Legend -->
            <div class="flex flex-wrap gap-4 mb-6" v-if="Object.keys(employeeStats).length > 0">
              <div 
                v-for="(employee, employeeName) in visibleEmployees" 
                :key="employeeName"
                class="flex items-center space-x-2 px-3 py-2 bg-[#f8f9fa] rounded-lg"
              >
                <div 
                  class="w-4 h-4 rounded-full" 
                  :style="{ backgroundColor: getEmployeeColor(employeeName) }"
                ></div>
                <span class="text-sm font-medium text-[#202124]">{{ employeeName }}</span>
                <span class="text-xs text-[#5f6368]">({{ employee.type === 'sales' ? 'Sales' : 'Service' }})</span>
              </div>
            </div>

            <!-- Chart Container -->
            <div class="h-96">
              <canvas v-if="employeeChartData.labels.length > 0" ref="employeeChartRef"></canvas>
              <div v-else class="h-full flex items-center justify-center text-[#5f6368]">
                <div class="text-center">
                  <svg class="h-12 w-12 mx-auto mb-4 text-[#dadce0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                  <p class="text-sm">No employee visit data available</p>
                  <p class="text-xs text-[#5f6368] mt-1">Employee visits will appear here once customers start using employee links</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee Data Table -->
          <div class="bg-white rounded-lg border border-[#dadce0]">
            <div class="px-6 py-4 border-b border-[#dadce0]">
              <h3 class="text-lg font-medium text-[#202124]">Employee Statistics</h3>
              <p class="text-sm text-[#5f6368]">Detailed performance data for all employees</p>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-[#dadce0]">
                <thead class="bg-[#f8f9fa]">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Employee</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Type</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Total Visits</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Positive Feedback</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Negative Feedback</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Neutral Feedback</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Last Activity</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-[#5f6368] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-[#dadce0]">
                  <tr v-if="Object.keys(sortedEmployees).length === 0">
                    <td colspan="8" class="px-6 py-12 text-center text-[#5f6368]">
                      <svg class="h-12 w-12 mx-auto mb-4 text-[#dadce0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"/>
                      </svg>
                      <p class="text-sm">No employee data available</p>
                      <p class="text-xs text-[#5f6368] mt-1">Employee data will appear here once customers visit through employee links</p>
                    </td>
                  </tr>
                  <tr 
                    v-for="(employee, employeeName) in sortedEmployees" 
                    :key="employeeName"
                    :class="employee.isHidden ? 'opacity-50 bg-[#f8f9fa]' : ''"
                    class="hover:bg-[#f8f9fa] transition-colors"
                  >
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div 
                          class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3"
                          :style="{ backgroundColor: getEmployeeColor(employeeName) }"
                        >
                          {{ employeeName.charAt(0).toUpperCase() }}
                        </div>
                        <div>
                          <div class="text-sm font-medium text-[#202124]">{{ employeeName }}</div>
                          <div v-if="employee.isHidden" class="text-xs text-[#d93025]">Hidden from chart</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="employee.type === 'sales' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'">
                        {{ employee.type === 'sales' ? '🏆 Sales' : '🔧 Service' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-[#202124]">{{ employee.visits }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-green-600">{{ employee.positive }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-red-600">{{ employee.negative }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ employee.neutral }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-[#5f6368]">
                      {{ employee.lastActivity ? formatDate(employee.lastActivity) : 'Never' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div class="flex items-center space-x-2">
                        <button 
                          @click="toggleEmployeeVisibility(employeeName)"
                          :class="employee.isHidden ? 'text-green-600 hover:text-green-500' : 'text-orange-600 hover:text-orange-500'"
                          class="transition-colors"
                        >
                          {{ employee.isHidden ? 'Show' : 'Hide' }}
                        </button>
                        <span class="text-[#dadce0]">|</span>
                        <button 
                          @click="deleteEmployeeData(employeeName)"
                          class="text-red-600 hover:text-red-500 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- QR Generator Tab -->
        <div v-if="activeTab === 'qr-generator'" class="space-y-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-medium text-[#202124]">QR Code Generator</h2>
          </div>
          <div class="bg-white rounded-lg border border-[#dadce0] p-6">
            <div class="h-64 flex items-center justify-center border-2 border-dashed border-[#dadce0] rounded-lg">
              <div class="text-center">
                <svg class="h-12 w-12 mx-auto mb-4 text-[#dadce0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z"/>
                </svg>
                <p class="text-[#5f6368]">QR code generation tools will be implemented here</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Email Settings Tab -->
        <div v-if="activeTab === 'email-settings'" class="space-y-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-medium text-[#202124]">Email Settings</h2>
          </div>

          <!-- Email Status Card -->
          <div class="bg-white rounded-lg border border-[#dadce0] p-6">
            <div class="flex items-center justify-between mb-6">
              <div>
                <h3 class="text-lg font-medium text-[#202124] mb-2">Email Notifications</h3>
                <p class="text-sm text-[#5f6368]">Control when and where feedback notification emails are sent</p>
              </div>
              <div class="flex items-center space-x-2">
                <span :class="emailSettings.enabled ? 'text-green-600' : 'text-red-600'" class="text-sm font-medium">
                  {{ emailSettings.enabled ? 'Enabled' : 'Disabled' }}
                </span>
                <button
                  @click="toggleEmailNotifications"
                  :class="[
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
                    emailSettings.enabled ? 'bg-green-600' : 'bg-gray-200'
                  ]"
                >
                  <span
                    :class="[
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      emailSettings.enabled ? 'translate-x-5' : 'translate-x-0'
                    ]"
                  />
                </button>
              </div>
            </div>

            <!-- Email Recipients Section -->
            <div v-if="emailSettings.enabled" class="space-y-4">
              <h4 class="text-md font-medium text-[#202124]">Email Recipients</h4>
              
              <!-- Current Recipients List -->
              <div class="space-y-2">
                <div
                  v-for="(email, index) in emailSettings.recipients"
                  :key="index"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div class="flex items-center space-x-3">
                    <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <div class="flex flex-col">
                      <span class="text-sm font-medium text-[#202124]">{{ email.address }}</span>
                      <div class="flex items-center space-x-2 mt-1">
                        <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{{ getTypeDisplayName(email.type) }}</span>
                        <span v-if="email.manager && email.manager !== 'all'" class="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                          {{ getManagerDisplayName(email.manager) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex items-center space-x-2">
                    <!-- Enable/Disable Toggle -->
                    <button
                      @click="toggleRecipientStatus(index)"
                      :class="[
                        'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                        email.enabled ? 'bg-green-600' : 'bg-gray-200'
                      ]"
                    >
                      <span
                        :class="[
                          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                          email.enabled ? 'translate-x-4' : 'translate-x-0'
                        ]"
                      />
                    </button>
                    
                    <!-- Remove Button -->
                    <button
                      @click="removeRecipient(index)"
                      class="text-red-600 hover:text-red-800 p-1"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Add New Recipient Form -->
              <div class="border-t pt-4">
                <h5 class="text-sm font-medium text-[#202124] mb-3">Add New Recipient</h5>
                <div class="space-y-3">
                  <!-- Email Address Input -->
                  <div class="flex space-x-2">
                    <input
                      v-model="newRecipient.address"
                      type="email"
                      placeholder="email@example.com"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <!-- Notification Type and Manager Selection -->
                  <div class="flex space-x-2">
                    <select
                      v-model="newRecipient.type"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Notifications</option>
                      <option value="positive">Positive Only</option>
                      <option value="negative">Complaints Only</option>
                      <option value="inquiry">Inquiries Only</option>
                    </select>
                    
                    <select
                      v-model="newRecipient.manager"
                      :disabled="newRecipient.type !== 'negative'"
                      :class="[
                        'flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                        newRecipient.type !== 'negative' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                      ]"
                    >
                      <option value="all">All Managers</option>
                      <option value="brynn">Brynn (Service Manager)</option>
                      <option value="dustin">Dustin (General Manager)</option>
                    </select>
                    
                    <button
                      @click="addRecipient"
                      :disabled="!newRecipient.address || !isValidEmail(newRecipient.address)"
                      class="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                  
                  <!-- Helper Text -->
                  <div class="text-xs text-gray-500">
                    <span v-if="newRecipient.type === 'negative'">
                      💡 Select a specific manager to receive only complaints assigned to them
                    </span>
                    <span v-else-if="newRecipient.type === 'all'">
                      📧 This recipient will receive all types of notifications
                    </span>
                    <span v-else>
                      📋 This recipient will only receive {{ newRecipient.type }} notifications
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Disabled State Message -->
            <div v-else class="text-center py-8">
              <svg class="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <p class="text-gray-500">Email notifications are currently disabled</p>
              <p class="text-sm text-gray-400 mt-1">Enable notifications to manage email recipients</p>
            </div>
          </div>

          <!-- Email Test Section -->
          <div v-if="emailSettings.enabled" class="bg-white rounded-lg border border-[#dadce0] p-6">
            <h3 class="text-lg font-medium text-[#202124] mb-4">Test Email</h3>
            <p class="text-sm text-[#5f6368] mb-4">Send a test email to verify your settings are working</p>
            
            <div class="flex space-x-2">
              <select
                v-model="testEmail.type"
                class="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="positive">Test Positive Feedback</option>
                <option value="negative">Test Complaint</option>
                <option value="inquiry">Test Inquiry</option>
              </select>
              <button
                @click="sendTestEmail"
                :disabled="isTestEmailSending"
                class="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <svg v-if="isTestEmailSending" class="animate-spin h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span>{{ isTestEmailSending ? 'Sending...' : 'Send Test Email' }}</span>
              </button>
            </div>

            <div v-if="testEmailResult" class="mt-3 p-3 rounded-md" :class="testEmailResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'">
              {{ testEmailResult.message }}
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Add Employee Modal -->
    <div 
      v-if="showAddEmployeeModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="showAddEmployeeModal = false"
    >
      <div 
        class="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        @click.stop
      >
        <h3 class="text-lg font-medium text-[#202124] mb-4">Add New Employee</h3>
        <form @submit.prevent="addEmployee">
          <div class="mb-4">
            <label class="block text-sm font-medium text-[#202124] mb-2">Employee Name</label>
            <input 
              v-model="newEmployee.name"
              type="text" 
              required
              class="w-full border border-[#dadce0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a73e8] focus:border-transparent"
              placeholder="Enter employee name"
            />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-[#202124] mb-2">Position (Optional)</label>
            <input 
              v-model="newEmployee.position"
              type="text" 
              class="w-full border border-[#dadce0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a73e8] focus:border-transparent"
              placeholder="e.g. Service Advisor, Technician"
            />
          </div>
          <div class="mb-6">
            <label class="block text-sm font-medium text-[#202124] mb-2">Employee Type</label>
            <select 
              v-model="newEmployee.type"
              class="w-full border border-[#dadce0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a73e8] focus:border-transparent"
            >
              <option value="service">Service Employee (emp parameter)</option>
              <option value="sales">Sales Employee (sales parameter)</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3">
            <button 
              type="button"
              @click="showAddEmployeeModal = false"
              class="px-4 py-2 text-[#5f6368] hover:text-[#202124] text-sm font-medium"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="bg-[#1a73e8] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#1557b0] transition-colors"
            >
              Add Employee
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div 
      v-if="showQRModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="showQRModal = false"
    >
      <div 
        class="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        @click.stop
      >
        <h3 class="text-lg font-medium text-[#202124] mb-4">QR Code - {{ selectedEmployee?.name }}</h3>
        <div class="text-center">
          <div class="bg-white p-4 rounded-lg border border-[#dadce0] mb-4 inline-block">
            <canvas ref="qrCanvas" class="max-w-full"></canvas>
          </div>
          <p class="text-sm text-[#5f6368] mb-4">{{ selectedEmployee?.url }}</p>
          <div class="flex justify-center space-x-3">
            <button 
              @click="downloadQR"
              class="bg-[#1a73e8] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#1557b0] transition-colors"
            >
              Download QR
            </button>
            <button 
              @click="copyEmployeeURL(selectedEmployee)"
              class="border border-[#dadce0] text-[#5f6368] px-4 py-2 rounded-md text-sm font-medium hover:bg-[#f8f9fa] transition-colors"
            >
              Copy URL
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import QRCode from 'qrcode'
import { Line, Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

const router = useRouter()
const { user, signOut, checkAuthStatus, isAuthenticated, isAdmin } = useAuth()

const activeTab = ref('overview')

const tabs = [
  { 
    id: 'overview', 
    name: 'Overview', 
    icon: 'ChartBarIcon'
  },
  { 
    id: 'analytics', 
    name: 'Analytics', 
    icon: 'ChartPieIcon'
  },
  { 
    id: 'tracking', 
    name: 'Employee Tracking', 
    icon: 'UsersIcon'
  },
  { 
    id: 'qr-generator', 
    name: 'QR Generator', 
    icon: 'QrCodeIcon'
  },
  {
    id: 'email-settings',
    name: 'Email Settings',
    icon: 'EmailIcon'
  }
]

// Sample data - will be replaced with real API calls
const stats = ref({
  total: 0,
  positive: 0,
  negative: 0,
  neutral: 0
})

const recentFeedback = ref([])

// Visitor statistics
const visitorStats = ref({
  total: 0,  // Will be loaded from API
  growth: 0
})

// Analytics and Charts
const selectedDateRange = ref(30) // Default to last 30 days
const customStartDate = ref('')
const customEndDate = ref('')
const periodStats = ref({
  total: 0,
  positive: 0,
  negative: 0,
  neutral: 0
})

// Chart data
const chartData = ref({
  labels: [],
  datasets: [
    {
      label: 'Positive Reviews',
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      borderColor: 'rgba(34, 197, 94, 1)',
      data: [],
      tension: 0.4
    },
    {
      label: 'Negative Reviews',
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      borderColor: 'rgba(239, 68, 68, 1)',
      data: [],
      tension: 0.4
    },
    {
      label: 'Total Visitors',
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 1)',
      data: [],
      tension: 0.4
    }
  ]
})

const pieChartData = ref({
  labels: ['Positive', 'Negative', 'Visitors'],
  datasets: [
    {
      data: [0, 0, 0],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(59, 130, 246, 0.8)'
      ],
      borderColor: [
        'rgba(34, 197, 94, 1)',
        'rgba(239, 68, 68, 1)',
        'rgba(59, 130, 246, 1)'
      ],
      borderWidth: 2
    }
  ]
})

const chartOptions = computed(() => {
  // Calculate number of days for x-axis tick limits
  let dayCount = 7
  if (selectedDateRange.value === 'custom' && customStartDate.value && customEndDate.value) {
    const start = new Date(customStartDate.value)
    const end = new Date(customEndDate.value)
    dayCount = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
  } else if (selectedDateRange.value !== 'custom') {
    dayCount = parseInt(selectedDateRange.value.toString()) || 7
  }
  
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: dayCount > 30 ? 10 : undefined
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  }
})

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
    }
  }
}

// Employee management
interface Employee {
  id: string
  name: string
  position: string
  url: string
  feedbackCount: number
  createdAt: string
  type?: 'service' | 'sales' // Track employee type
}

const employees = ref<Employee[]>([])
const showAddEmployeeModal = ref(false)
const showQRModal = ref(false)
const selectedEmployee = ref<Employee | null>(null)
const qrCanvas = ref<HTMLCanvasElement | null>(null)
const newEmployee = ref({
  name: '',
  position: '',
  type: 'service' // Default to service employee
})

// Email Settings Management
interface EmailRecipient {
  address: string
  type: 'all' | 'positive' | 'negative' | 'inquiry'
  manager?: 'all' | 'brynn' | 'dustin' // New: manager-specific filtering
  enabled: boolean
}

const emailSettings = ref({
  enabled: true,
  recipients: [
    { address: 'henry@saskatoonauto.ca', type: 'all' as const, manager: 'all' as const, enabled: true },
    { address: 'dustin@saskatoonauto.ca', type: 'negative' as const, manager: 'dustin' as const, enabled: true },
    { address: 'brynn@saskatoonauto.ca', type: 'negative' as const, manager: 'brynn' as const, enabled: true }
  ] as EmailRecipient[]
})

const newRecipient = ref({
  address: '',
  type: 'all' as const,
  manager: 'all' as const
})

const testEmail = ref({
  type: 'positive' as const
})

const isTestEmailSending = ref(false)
const testEmailResult = ref<{ success: boolean, message: string } | null>(null)

// Employee tracking variables
const employeeStats = ref<Record<string, any>>({})
const employeeChartPeriod = ref(7)
const employeeCustomStartDate = ref('')
const employeeCustomEndDate = ref('')
const employeeChartData = ref<{
  labels: string[]
  datasets: any[]
}>({
  labels: [],
  datasets: []
})
const employeeChart = ref<any>(null)
const employeeChartRef = ref<HTMLCanvasElement | null>(null)

// Display user for UI (real user in production, fake in development)
const displayUser = computed(() => {
  if (user.value) {
    return user.value
  }
  // Development fallback
  if (import.meta.env.DEV) {
    return {
      name: 'Henry Kim',
      email: 'henry@saskatoonauto.ca',
      picture: null
    }
  }
  return null
})

const positivePercentage = computed(() => {
  return stats.value.total > 0 ? Math.round((stats.value.positive / stats.value.total) * 100) : 0
})

const negativePercentage = computed(() => {
  return stats.value.total > 0 ? Math.round((stats.value.negative / stats.value.total) * 100) : 0
})

const neutralPercentage = computed(() => {
  return stats.value.total > 0 ? Math.round(((stats.value.neutral || 0) / stats.value.total) * 100) : 0
})

// Employee tracking computed properties
const visibleEmployees = computed(() => {
  return Object.fromEntries(
    Object.entries(employeeStats.value).filter(([_, employee]) => !employee.isHidden)
  )
})

// Sorted employees by total visits (descending)
const sortedEmployees = computed(() => {
  return Object.entries(employeeStats.value)
    .sort(([, a], [, b]) => (b.visits || 0) - (a.visits || 0))
    .reduce((acc, [name, employee]) => {
      acc[name] = employee
      return acc
    }, {} as Record<string, any>)
})

const employeeColors = ['#1a73e8', '#34a853', '#ea4335', '#fbbc04', '#9c27b0', '#ff9800', '#795548', '#607d8b']

const getEmployeeColor = (employeeName: string) => {
  const employees = Object.keys(employeeStats.value)
  const index = employees.indexOf(employeeName)
  return employeeColors[index % employeeColors.length]
}

const handleSignOut = async () => {
  signOut()
  router.push('/admin/login')
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
}

const formatDate = (timestamp: string) => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const addEmployee = async () => {
  if (!newEmployee.value.name.trim()) return
  
  // Generate URL based on employee type
  const employeeType = newEmployee.value.type || 'service'
  const urlParam = employeeType === 'sales' ? 'sales' : 'emp'
  const employeeName = newEmployee.value.name.toLowerCase().replace(/\s+/g, '')
  
  const employee = {
    id: Date.now().toString(),
    name: newEmployee.value.name.trim(),
    position: newEmployee.value.position.trim() || 'Employee',
    url: `${window.location.origin}/f?${urlParam}=${encodeURIComponent(employeeName)}`,
    feedbackCount: 0,
    createdAt: new Date().toISOString(),
    type: employeeType
  }
  
  try {
    const response = await fetch('/api/employee/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(employee)
    })
    
    if (response.ok) {
      employees.value.push(employee)
      console.log('✅ Employee added:', employee)
    } else {
      // Fallback to local storage if API fails
      const savedEmployees = JSON.parse(localStorage.getItem('sac_employees') || '[]')
      savedEmployees.push(employee)
      localStorage.setItem('sac_employees', JSON.stringify(savedEmployees))
      employees.value.push(employee)
    }
    
    // Reset form and close modal
    newEmployee.value = { name: '', position: '', type: 'service' }
    showAddEmployeeModal.value = false
    
  } catch (error) {
    console.error('Error adding employee:', error)
    // Fallback to local storage
    const savedEmployees = JSON.parse(localStorage.getItem('sac_employees') || '[]')
    savedEmployees.push(employee)
    localStorage.setItem('sac_employees', JSON.stringify(savedEmployees))
    employees.value.push(employee)
    
    newEmployee.value = { name: '', position: '', type: 'service' }
    showAddEmployeeModal.value = false
  }
}

const viewEmployeeQR = async (employee: Employee) => {
  selectedEmployee.value = employee
  showQRModal.value = true
  
  await nextTick()
  
  if (qrCanvas.value) {
    try {
      await QRCode.toCanvas(qrCanvas.value, employee.url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      })
    } catch (error) {
      console.error('Error generating QR code:', error)
    }
  }
}

const copyEmployeeURL = async (employee: Employee) => {
  try {
    await navigator.clipboard.writeText(employee.url)
    // You could add a toast notification here
    console.log('URL copied to clipboard:', employee.url)
  } catch (error) {
    console.error('Failed to copy URL:', error)
  }
}

const downloadQR = () => {
  if (qrCanvas.value && selectedEmployee.value) {
    const link = document.createElement('a')
    link.download = `${selectedEmployee.value.name}-qr-code.png`
    link.href = qrCanvas.value.toDataURL()
    link.click()
  }
}

const deleteEmployee = async (employeeId: string) => {
  if (!confirm('Are you sure you want to delete this employee?')) return
  
  try {
    const response = await fetch(`/api/employee/delete/${employeeId}`, {
      method: 'DELETE'
    })
    
    if (response.ok || response.status === 404) {
      employees.value = employees.value.filter(emp => emp.id !== employeeId)
    } else {
      // Fallback to local storage
      const savedEmployees = JSON.parse(localStorage.getItem('sac_employees') || '[]')
      const filteredEmployees = savedEmployees.filter((emp: Employee) => emp.id !== employeeId)
      localStorage.setItem('sac_employees', JSON.stringify(filteredEmployees))
      employees.value = employees.value.filter(emp => emp.id !== employeeId)
    }
    
  } catch (error) {
    console.error('Error deleting employee:', error)
    // Fallback to local storage
    const savedEmployees = JSON.parse(localStorage.getItem('sac_employees') || '[]')
    const filteredEmployees = savedEmployees.filter(emp => emp.id !== employeeId)
    localStorage.setItem('sac_employees', JSON.stringify(filteredEmployees))
    employees.value = employees.value.filter(emp => emp.id !== employeeId)
  }
}

const loadEmployees = async () => {
  try {
    const response = await fetch('/api/employee/list')
    if (response.ok) {
      const data = await response.json()
      employees.value = data.employees || []
    } else {
      // Fallback to local storage
      const savedEmployees = JSON.parse(localStorage.getItem('sac_employees') || '[]')
      employees.value = savedEmployees
    }
  } catch (error) {
    console.error('Error loading employees:', error)
    // Fallback to local storage
    const savedEmployees = JSON.parse(localStorage.getItem('sac_employees') || '[]')
    employees.value = savedEmployees
  }
}

// Employee tracking functions
const loadEmployeeStats = async () => {
  try {
    // Build query parameters for employee stats
    let queryParams = 'employee=true'
    
    if (employeeChartPeriod.value === 'custom' && employeeCustomStartDate.value && employeeCustomEndDate.value) {
      queryParams += `&startDate=${employeeCustomStartDate.value}&endDate=${employeeCustomEndDate.value}`
    } else if (employeeChartPeriod.value !== 'custom') {
      queryParams += `&period=${employeeChartPeriod.value}`
    }
    
    const response = await fetch(`/api/dashboard/simple-stats?${queryParams}`)
    if (response.ok) {
      const data = await response.json()
      // Convert to plain object to avoid Proxy issues with Vue reactivity
      const employees = data.employees || {}
      employeeStats.value = JSON.parse(JSON.stringify(employees))
      console.log('📊 Employee stats loaded:', employeeStats.value)
      console.log('📊 Number of employees:', Object.keys(employeeStats.value).length)
      await updateEmployeeChart()
    }
  } catch (error) {
    console.error('Failed to load employee stats:', error)
  }
}

const refreshEmployeeData = async () => {
  console.log('🔄 Refreshing employee data...')
  await loadEmployeeStats()
}

const updateEmployeeChart = async () => {
  try {
    const periodText = employeeChartPeriod.value === 'custom' ? 'custom period' : `${employeeChartPeriod.value} days`
    console.log(`📊 Updating employee chart for ${periodText}`)
    
    // Destroy existing chart first with better error handling
    if (employeeChart.value) {
      try {
        employeeChart.value.destroy()
        employeeChart.value = null
        console.log('📊 Previous chart destroyed')
      } catch (error) {
        console.warn('📊 Warning destroying chart:', error)
        employeeChart.value = null
      }
    }
    
    // Wait a bit to ensure cleanup
    await new Promise(resolve => setTimeout(resolve, 50))
    
    if (Object.keys(employeeStats.value).length === 0) {
      console.log('📊 No employee data available')
      employeeChartData.value = { labels: [], datasets: [] }
      return
    }

    // Generate date labels for the selected period
    const labels = []
    let startDate: Date
    let endDate: Date
    
    if (employeeChartPeriod.value === 'custom' && employeeCustomStartDate.value && employeeCustomEndDate.value) {
      startDate = new Date(employeeCustomStartDate.value)
      endDate = new Date(employeeCustomEndDate.value)
    } else {
      const today = new Date()
      const days = parseInt(employeeChartPeriod.value as string) || 7
      startDate = new Date(today)
      startDate.setDate(today.getDate() - (days - 1))
      endDate = today
    }
    
    console.log(`📊 Generating labels from ${startDate.toDateString()} to ${endDate.toDateString()}`)
    
    // Generate labels between start and end dates
    const currentDate = new Date(startDate)
    while (currentDate <= endDate) {
      const daysDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
      const label = daysDiff <= 7 
        ? currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : currentDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      labels.push(label)
      currentDate.setDate(currentDate.getDate() + 1)
    }
    
    console.log('📊 Generated labels:', labels.slice(0, 5), '...')

    // Generate datasets for each visible employee
    const datasets = []
    const visibleEmpEntries = Object.entries(visibleEmployees.value)
    
    console.log(`📊 Processing ${visibleEmpEntries.length} visible employees`)
    
    visibleEmpEntries.forEach(([employeeName, employee], index) => {
      const data = []
      let totalVisits = 0
      
      // Get visit data for each day in the range
      const dataCurrentDate = new Date(startDate)
      while (dataCurrentDate <= endDate) {
        const dateStr = dataCurrentDate.toISOString().split('T')[0]
        const visits = employee.dailyVisits?.[dateStr] || 0
        data.push(visits)
        totalVisits += visits
        dataCurrentDate.setDate(dataCurrentDate.getDate() + 1)
      }

      // Only add dataset if employee has some data or we want to show zero lines
      const daysDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
      if (totalVisits > 0 || daysDiff <= 7) {
        datasets.push({
          label: `${employeeName} (${employee.type})`,
          data: data,
          borderColor: getEmployeeColor(employeeName),
          backgroundColor: getEmployeeColor(employeeName) + '20',
          tension: 0.4,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2
        })
        console.log(`📊 Added dataset for ${employeeName}: ${totalVisits} total visits`)
      }
    })

    if (datasets.length === 0) {
      console.log('📊 No datasets with data, creating empty chart')
      // Create a default dataset to prevent empty chart errors
      datasets.push({
        label: 'No Data',
        data: new Array(labels.length).fill(0),
        borderColor: '#e5e7eb',
        backgroundColor: '#e5e7eb20',
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        borderWidth: 1
      })
    }

    employeeChartData.value = { labels, datasets }
    console.log(`📊 Chart data prepared: ${labels.length} labels, ${datasets.length} datasets`)
    
    // Wait for Vue to update the DOM
    await nextTick()
    
    // Additional wait to ensure canvas is ready
    await new Promise(resolve => setTimeout(resolve, 100))
    
    if (employeeChartRef.value) {
      const ctx = employeeChartRef.value.getContext('2d')
      
      if (ctx) {
        try {
          // Ensure canvas is properly sized
          const canvas = employeeChartRef.value
          canvas.width = canvas.offsetWidth
          canvas.height = canvas.offsetHeight
          
          console.log('📊 Creating new chart...')
          
          employeeChart.value = new ChartJS(ctx, {
            type: 'line',
            data: employeeChartData.value,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              animation: {
                duration: 750,
                easing: 'easeInOutQuart'
              },
              plugins: {
                legend: {
                  display: false // We have our own legend
                },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  titleColor: '#fff',
                  bodyColor: '#fff',
                  borderColor: '#fff',
                  borderWidth: 1,
                  callbacks: {
                    title: function(context) {
                      return `Date: ${context[0].label}`
                    },
                    label: function(context) {
                      return `${context.dataset.label}: ${context.parsed.y} visits`
                    }
                  }
                }
              },
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true,
                    text: 'Date',
                    color: '#6b7280'
                  },
                  ticks: {
                    color: '#6b7280',
                    maxTicksLimit: daysDiff > 30 ? 10 : undefined
                  },
                  grid: {
                    color: '#f3f4f6'
                  }
                },
                y: {
                  display: true,
                  title: {
                    display: true,
                    text: 'Visits',
                    color: '#6b7280'
                  },
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1,
                    color: '#6b7280'
                  },
                  grid: {
                    color: '#f3f4f6'
                  }
                }
              },
              interaction: {
                mode: 'nearest',
                axis: 'x',
                intersect: false
              },
              elements: {
                point: {
                  hoverBackgroundColor: '#fff',
                  hoverBorderWidth: 2
                }
              }
            }
          })
          
          console.log('📊 Chart created successfully')
          
        } catch (chartError) {
          console.error('📊 Error creating chart:', chartError)
          console.error('📊 Chart data:', employeeChartData.value)
          console.error('📊 Canvas context:', ctx)
        }
      } else {
        console.error('📊 Failed to get canvas context')
      }
    } else {
      console.error('📊 Chart canvas ref not available')
    }
  } catch (error) {
    console.error('📊 Failed to update employee chart:', error)
    console.error('📊 Error stack:', error.stack)
  }
}

const toggleEmployeeVisibility = async (employeeName: string) => {
  try {
    const response = await fetch('/api/dashboard/simple-stats?employee=true', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employeeName,
        action: 'toggle_hide'
      })
    })
    
    if (response.ok) {
      await loadEmployeeStats()
    }
  } catch (error) {
    console.error('Failed to toggle employee visibility:', error)
  }
}

const deleteEmployeeData = async (employeeName: string) => {
  if (!confirm(`Are you sure you want to delete all data for ${employeeName}? This action cannot be undone.`)) {
    return
  }
  
  try {
    console.log('🗑️ Attempting to delete employee:', employeeName)
    const response = await fetch('/api/dashboard/simple-stats?employee=true', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employeeName,
        action: 'delete'
      })
    })
    
    console.log('🗑️ Delete response status:', response.status)
    
    if (response.ok) {
      const result = await response.json()
      console.log('✅ Employee deleted successfully:', result)
      
      // Force update the local state immediately
      const updatedStats = { ...employeeStats.value }
      delete updatedStats[employeeName]
      employeeStats.value = updatedStats
      console.log('🔄 Local state updated, remaining employees:', Object.keys(employeeStats.value))
      
      // Also reload from server to ensure sync
      await loadEmployeeStats()
      console.log('🔄 Employee stats reloaded after deletion')
      
      // Force chart update
      await updateEmployeeChart()
      console.log('📊 Chart updated after deletion')
    } else {
      const errorText = await response.text()
      console.error('❌ Delete failed:', response.status, errorText)
      alert(`Failed to delete employee: ${response.status}`)
    }
  } catch (error) {
    console.error('❌ Failed to delete employee data:', error)
    alert('Error occurred while deleting employee data.')
  }
}

const refreshStats = async () => {
  try {
    console.log('🔄 Refreshing stats...')
    const response = await fetch('/api/dashboard/simple-stats')
    if (response.ok) {
      const data = await response.json()
      console.log('📊 Refresh response:', data)
      if (data.stats) {
        console.log('📊 Refresh stats details:', {
          total: data.stats.total,
          positive: data.stats.positive,
          negative: data.stats.negative,
          neutral: data.stats.neutral,
          lastUpdated: data.stats.lastUpdated
        })
        console.log('📊 Refresh feedback count:', data.recentFeedback?.length || 0)
        
        stats.value = data.stats
        recentFeedback.value = data.recentFeedback || []
        console.log('📊 Stats refreshed successfully - new values applied')
      }
    } else {
      console.error('📊 Refresh failed:', response.status)
    }
  } catch (error) {
    console.error('Error refreshing stats:', error)
  }
}

const loadDashboardData = async () => {
  try {
    console.log('🔄 Loading dashboard data from API...')
    
    // Try the simple stats API
    try {
      console.log('📊 Fetching from /api/dashboard/simple-stats')
      const response = await fetch('/api/dashboard/simple-stats')
      console.log('📊 Response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('📊 Raw API response:', data)
        console.log('📊 Stats object:', data.stats)
        
        if (data.stats) {
          console.log('📊 Stats details:', {
            total: data.stats.total,
            positive: data.stats.positive,
            negative: data.stats.negative,
            neutral: data.stats.neutral,
            lastUpdated: data.stats.lastUpdated
          })
          console.log('📊 Recent feedback count:', data.recentFeedback?.length || 0)
          
          stats.value = data.stats
          recentFeedback.value = data.recentFeedback || []
          console.log('✅ Real dashboard data loaded successfully')
          return
        } else {
          console.log('⚠️ No stats in response')
        }
      } else {
        const errorText = await response.text()
        console.log('📊 API response not OK:', response.status)
        console.log('📊 Error response:', errorText.substring(0, 200))
      }
    } catch (apiError) {
      console.error('📊 API call failed:', apiError)
    }
    
    // Initialize with empty data if API fails
    stats.value = {
      total: 0,
      positive: 0,
      negative: 0,
      neutral: 0,
      lastUpdated: new Date().toISOString()
    }
    recentFeedback.value = []
    console.log('📊 Dashboard initialized with empty data - waiting for real feedback')
    
  } catch (error) {
    console.error('Error loading dashboard data:', error)
  }
}

// Feedback Management Functions
const deleteFeedback = async (feedbackId: string) => {
  if (!confirm('이 피드백을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
    return
  }
  
  try {
    console.log('🗑️ Deleting feedback:', feedbackId)
    
    // Call the API to delete the feedback
    const response = await fetch('/api/dashboard/simple-stats', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        feedbackId: feedbackId
      })
    })
    
    if (response.ok) {
      const result = await response.json()
      console.log('✅ Feedback deleted successfully:', result)
      
      // Update local state immediately
      recentFeedback.value = recentFeedback.value.filter(feedback => feedback.id !== feedbackId)
      
      // Update stats if returned from API
      if (result.stats) {
        stats.value = result.stats
      }
      
      // Reload data to ensure everything is in sync
      await loadDashboardData()
      
    } else {
      const errorText = await response.text()
      console.error('❌ Failed to delete feedback:', response.status, errorText)
      alert('피드백 삭제에 실패했습니다. 다시 시도해주세요.')
    }
  } catch (error) {
    console.error('❌ Error deleting feedback:', error)
    alert('피드백 삭제 중 오류가 발생했습니다.')
  }
}

// Email Settings Functions
const toggleEmailNotifications = () => {
  emailSettings.value.enabled = !emailSettings.value.enabled
  saveEmailSettings()
}

const toggleRecipientStatus = (index: number) => {
  emailSettings.value.recipients[index].enabled = !emailSettings.value.recipients[index].enabled
  saveEmailSettings()
}

const removeRecipient = (index: number) => {
  if (confirm('이 이메일 수신자를 삭제하시겠습니까?')) {
    emailSettings.value.recipients.splice(index, 1)
    saveEmailSettings()
  }
}

const addRecipient = () => {
  if (!newRecipient.value.address.trim() || !isValidEmail(newRecipient.value.address)) {
    return
  }
  
  // Check for duplicate email
  const isDuplicate = emailSettings.value.recipients.some(
    recipient => recipient.address.toLowerCase() === newRecipient.value.address.toLowerCase()
  )
  
  if (isDuplicate) {
    alert('이미 등록된 이메일 주소입니다.')
    return
  }
  
  // Set manager to 'all' if not negative type
  const manager = newRecipient.value.type === 'negative' ? newRecipient.value.manager : 'all'
  
  emailSettings.value.recipients.push({
    address: newRecipient.value.address,
    type: newRecipient.value.type,
    manager: manager,
    enabled: true
  })
  
  // Reset form
  newRecipient.value = {
    address: '',
    type: 'all',
    manager: 'all'
  }
  
  saveEmailSettings()
}

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const getTypeDisplayName = (type: string) => {
  switch (type) {
    case 'all': return 'All Notifications'
    case 'positive': return 'Positive Only'
    case 'negative': return 'Complaints Only'
    case 'inquiry': return 'Inquiries Only'
    default: return type
  }
}

const getManagerDisplayName = (manager: string) => {
  switch (manager) {
    case 'brynn': return 'Brynn (Service Mgr)'
    case 'dustin': return 'Dustin (General Mgr)'
    case 'all': return 'All Managers'
    default: return manager
  }
}

const saveEmailSettings = async () => {
  try {
    // Save to API endpoint
    const response = await fetch('/api/admin/email-settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(emailSettings.value)
    })
    
    if (response.ok) {
      const result = await response.json()
      if (result.success) {
        console.log('📧 Email settings saved successfully:', emailSettings.value)
        // Save to localStorage as backup
        localStorage.setItem('sac_email_settings', JSON.stringify(emailSettings.value))
      } else {
        throw new Error(result.message || 'Failed to save email settings')
      }
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
  } catch (error) {
    console.error('이메일 설정 저장 실패:', error)
    alert('이메일 설정 저장에 실패했습니다: ' + (error instanceof Error ? error.message : error))
  }
}

const loadEmailSettings = async () => {
  try {
    // Load from API endpoint
    const response = await fetch('/api/admin/email-settings', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const result = await response.json()
      if (result.success && result.data) {
        emailSettings.value = result.data
        console.log('📧 Email settings loaded from API:', emailSettings.value)
        // Save to localStorage as backup
        localStorage.setItem('sac_email_settings', JSON.stringify(emailSettings.value))
        return
      }
    }
    
    // Fallback to localStorage if API fails
    const saved = localStorage.getItem('sac_email_settings')
    if (saved) {
      emailSettings.value = JSON.parse(saved)
      console.log('📧 Email settings loaded from localStorage (fallback)')
    }
  } catch (error) {
    console.error('이메일 설정 로드 실패:', error)
    // Try localStorage as fallback
    try {
      const saved = localStorage.getItem('sac_email_settings')
      if (saved) {
        emailSettings.value = JSON.parse(saved)
        console.log('📧 Email settings loaded from localStorage (error fallback)')
      }
    } catch (localStorageError) {
      console.error('localStorage 설정도 로드 실패:', localStorageError)
    }
  }
}

const sendTestEmail = async () => {
  if (isTestEmailSending.value) return
  
  isTestEmailSending.value = true
  testEmailResult.value = null
  
  try {
    // Get active recipients for this email type
    const activeRecipients = emailSettings.value.recipients.filter(recipient => {
      if (!recipient.enabled) return false
      if (recipient.type === 'all') return true
      if (testEmail.value.type === 'positive' && recipient.type === 'positive') return true
      if (testEmail.value.type === 'negative' && recipient.type === 'negative') {
        // For negative emails, check manager assignment
        // For test emails, we'll use 'brynn' as default test manager
        if (recipient.manager === 'all' || recipient.manager === 'brynn') return true
      }
      if (testEmail.value.type === 'inquiry' && recipient.type === 'inquiry') return true
      return false
    }).map(recipient => recipient.address)
    
    if (activeRecipients.length === 0) {
      testEmailResult.value = {
        success: false,
        message: '이 유형의 이메일을 받을 수신자가 없습니다.'
      }
      return
    }
    
    // Create test data based on email type
    let testData = {}
    
    if (testEmail.value.type === 'positive') {
      testData = {
        customer: { name: '테스트 고객', service: '오일 교환', date: new Date().toLocaleDateString() },
        feedback: { text: '정말 만족스러운 서비스였습니다!', rating: 5, sentiment: 'positive' },
        recipients: activeRecipients,
        type: 'positive_test'
      }
    } else if (testEmail.value.type === 'negative') {
      testData = {
        customer: { name: '테스트 고객', service: '엔진 수리', date: new Date().toLocaleDateString() },
        feedback: { text: '서비스에 불만족합니다.', rating: 1, sentiment: 'negative' },
        complaint: { details: '대기시간이 너무 길었습니다.', priority: 'normal', manager: 'brynn' },
        recipients: activeRecipients,
        type: 'complaint_test'
      }
    } else {
      testData = {
        customer: { name: '테스트 고객', service: '일반 문의', date: new Date().toLocaleDateString() },
        feedback: { text: '운영시간을 알고 싶습니다.', sentiment: 'inquiry' },
        inquiry: { originalQuestion: '주말에도 영업하시나요?' },
        recipients: activeRecipients,
        type: 'inquiry_test'
      }
    }
    
    const response = await fetch('/api/feedback/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(testData)
    })
    
    if (response.ok) {
      testEmailResult.value = {
        success: true,
        message: `테스트 이메일이 성공적으로 발송되었습니다. (수신자 ${activeRecipients.length}명)`
      }
    } else {
      const errorText = await response.text()
      testEmailResult.value = {
        success: false,
        message: `이메일 발송 실패: ${response.status} ${errorText}`
      }
    }
  } catch (error) {
    console.error('테스트 이메일 발송 오류:', error)
    testEmailResult.value = {
      success: false,
      message: '이메일 발송 중 오류가 발생했습니다.'
    }
  } finally {
    isTestEmailSending.value = false
    
    // Clear result after 5 seconds
    setTimeout(() => {
      testEmailResult.value = null
    }, 5000)
  }
}

// Load real visitor data
async function loadVisitorData() {
  try {
    // Build query parameters based on date range selection
    let queryParams = 'visitor=true'
    
    if (selectedDateRange.value === 'custom' && customStartDate.value && customEndDate.value) {
      queryParams += `&startDate=${customStartDate.value}&endDate=${customEndDate.value}`
    } else if (selectedDateRange.value !== 'custom') {
      queryParams += `&period=${selectedDateRange.value}`
    }
    
    const response = await fetch(`/api/dashboard/simple-stats?${queryParams}`)
    if (response.ok) {
      const data = await response.json()
      visitorStats.value = {
        total: data.total,
        growth: data.growth
      }
      console.log('📊 Real visitor data loaded:', data)
      console.log('📊 Visitor API URL:', `/api/dashboard/simple-stats?${queryParams}`)
    }
  } catch (error) {
    console.error('📊 Failed to load visitor data:', error)
  }
}

// Load real feedback data for analytics
async function loadRealAnalyticsData() {
  try {
    // Build query parameters based on date range selection
    let queryParams = ''
    
    if (selectedDateRange.value === 'custom' && customStartDate.value && customEndDate.value) {
      queryParams = `?startDate=${customStartDate.value}&endDate=${customEndDate.value}`
    } else if (selectedDateRange.value !== 'custom') {
      queryParams = `?period=${selectedDateRange.value}`
    }
    
    // Load both feedback and visitor data simultaneously
    const [feedbackResponse, visitorResponse] = await Promise.all([
      fetch(`/api/dashboard/simple-stats${queryParams}`),
      fetch(`/api/dashboard/simple-stats?visitor=true${queryParams ? '&' + queryParams.substring(1) : ''}`)
    ])
    
    if (feedbackResponse.ok) {
      const data = await feedbackResponse.json()
      
      // Use real stats for period summary
      periodStats.value = {
        total: data.stats.total,
        positive: data.stats.positive,
        negative: data.stats.negative,
        neutral: data.stats.neutral
      }
      
      console.log('📊 Real analytics data loaded:', data)
      console.log('📊 Analytics API URL:', `/api/dashboard/simple-stats${queryParams}`)
      console.log('📊 Period stats updated:', periodStats.value)
      console.log('📊 Feedback data count:', (data.recentFeedback || []).length)
      
      // Load visitor data if available
      if (visitorResponse.ok) {
        const visitorData = await visitorResponse.json()
        console.log('📊 Visitor data loaded:', visitorData)
        console.log('📊 Visitor API URL:', `/api/dashboard/simple-stats?visitor=true${queryParams ? '&' + queryParams.substring(1) : ''}`)
        if (visitorData.total !== undefined) {
          visitorStats.value = {
            total: visitorData.total,
            growth: visitorData.growth || 0,
            daily: visitorData.daily || {},
            source: visitorData.source || 'memory'
          }
          console.log('📊 Visitor data loaded for analytics:', visitorStats.value)
        }
      }
      
      // Generate time series from recent feedback and visitor data
      console.log('📊 Generating time series with feedback data:', (data.recentFeedback || []).length, 'items')
      generateTimeSeriesFromRealData(data.recentFeedback || [])
    }
  } catch (error) {
    console.error('📊 Failed to load real analytics data:', error)
    generateMockTimeSeriesData() // Fallback to mock data
  }
}

function generateTimeSeriesFromRealData(feedbackData: any[]) {
  const labels = []
  const positiveData = []
  const negativeData = []
  const visitorData = []
  
  // Create date buckets
  const dateBuckets: { [date: string]: { positive: number, negative: number, visitors: number } } = {}
  
  // Determine date range based on selection
  let startDate: Date
  let endDate: Date
  
  if (selectedDateRange.value === 'custom' && customStartDate.value && customEndDate.value) {
    startDate = new Date(customStartDate.value)
    endDate = new Date(customEndDate.value)
  } else {
    const days = parseInt(selectedDateRange.value.toString()) || 7
    endDate = new Date()
    startDate = new Date()
    startDate.setDate(endDate.getDate() - (days - 1))
  }
  
  console.log(`📊 Generating chart data from ${startDate.toDateString()} to ${endDate.toDateString()}`)
  
  // Initialize buckets for the selected period
  const currentDate = new Date(startDate)
  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split('T')[0]
    const daysDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
    const labelStr = daysDiff <= 7 
      ? currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : currentDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
    
    labels.push(labelStr)
    dateBuckets[dateStr] = { positive: 0, negative: 0, visitors: 0 }
    currentDate.setDate(currentDate.getDate() + 1)
  }
  
  // Count feedback by date and type
  console.log('📊 Processing feedback data:', feedbackData.length, 'items')
  feedbackData.forEach((feedback, index) => {
    if (!feedback.timestamp) {
      console.log(`📊 Feedback ${index} missing timestamp:`, feedback)
      return
    }
    
    const feedbackDate = new Date(feedback.timestamp).toISOString().split('T')[0]
    console.log(`📊 Feedback ${index}: date=${feedbackDate}, type=${feedback.type}, bucket exists=${!!dateBuckets[feedbackDate]}`)
    
    if (dateBuckets[feedbackDate]) {
      if (feedback.type === 'positive') {
        dateBuckets[feedbackDate].positive++
      } else if (feedback.type === 'negative') {
        dateBuckets[feedbackDate].negative++
      }
    }
  })
  
  console.log('📊 Final date buckets:', dateBuckets)
  
  // Add visitor data from visitorStats
  if (visitorStats.value && visitorStats.value.daily) {
    Object.keys(dateBuckets).forEach(date => {
      dateBuckets[date].visitors = visitorStats.value.daily[date] || 0
    })
  }
  
  // Convert to arrays for chart (maintain order by dates)
  const sortedDates = Object.keys(dateBuckets).sort()
  sortedDates.forEach(date => {
    positiveData.push(dateBuckets[date].positive)
    negativeData.push(dateBuckets[date].negative)
    visitorData.push(dateBuckets[date].visitors)
  })
  
  console.log('📊 Chart arrays prepared - Positive:', positiveData, 'Negative:', negativeData, 'Visitors:', visitorData)
  
  // Update chart data
  chartData.value.labels = labels
  chartData.value.datasets[0].data = positiveData
  chartData.value.datasets[1].data = negativeData
  chartData.value.datasets[2].data = visitorData
  
  // Update pie chart data - scale visitor data to make it comparable
  const totalPositive = positiveData.reduce((sum, val) => sum + val, 0)
  const totalNegative = negativeData.reduce((sum, val) => sum + val, 0)
  const totalVisitors = visitorData.reduce((sum, val) => sum + val, 0)
  
  // Scale visitors to be proportional to feedback for pie chart
  const scaledVisitors = Math.round(totalVisitors / 10) // Scale down visitors for better pie chart proportion
  
  pieChartData.value.datasets[0].data = [totalPositive, totalNegative, scaledVisitors]
}

// Analytics functions
function generateMockTimeSeriesData() {
  const labels = []
  const positiveData = []
  const negativeData = []
  const neutralData = []
  
  let totalPositive = 0
  let totalNegative = 0
  let totalNeutral = 0
  
  // Determine date range based on selection
  let startDate: Date
  let endDate: Date
  
  if (selectedDateRange.value === 'custom' && customStartDate.value && customEndDate.value) {
    startDate = new Date(customStartDate.value)
    endDate = new Date(customEndDate.value)
  } else {
    const days = parseInt(selectedDateRange.value.toString()) || 7
    endDate = new Date()
    startDate = new Date()
    startDate.setDate(endDate.getDate() - (days - 1))
  }
  
  console.log(`📊 Generating mock chart data from ${startDate.toDateString()} to ${endDate.toDateString()}`)
  
  // Generate data for each day in the range
  const currentDate = new Date(startDate)
  while (currentDate <= endDate) {
    const daysDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
    const labelStr = daysDiff <= 7 
      ? currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : currentDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
    
    labels.push(labelStr)
    
    // Generate mock data with some randomness
    const positive = Math.floor(Math.random() * 8) + 1
    const negative = Math.floor(Math.random() * 3)
    const neutral = Math.floor(Math.random() * 2)
    
    positiveData.push(positive)
    negativeData.push(negative)
    neutralData.push(neutral)
    
    totalPositive += positive
    totalNegative += negative
    totalNeutral += neutral
    
    currentDate.setDate(currentDate.getDate() + 1)
  }
  
  // Update chart data
  chartData.value.labels = labels
  chartData.value.datasets[0].data = positiveData
  chartData.value.datasets[1].data = negativeData
  chartData.value.datasets[2].data = neutralData
  
  // Update pie chart data
  pieChartData.value.datasets[0].data = [totalPositive, totalNegative, totalNeutral]
  
  // Update period stats
  periodStats.value = {
    total: totalPositive + totalNegative + totalNeutral,
    positive: totalPositive,
    negative: totalNegative,
    neutral: totalNeutral
  }
}

function updateChartData() {
  console.log('📊 Updating chart data for period:', selectedDateRange.value)
  // Load both visitor data and analytics data with current filters
  loadVisitorData()
  loadRealAnalyticsData()
}

function refreshAnalytics() {
  console.log('📊 Refreshing analytics data')
  // Load visitor data first, then analytics data
  loadVisitorData().then(() => {
    loadRealAnalyticsData()
  })
}

// Watch for tab changes to update analytics
watch(activeTab, (newTab) => {
  if (newTab === 'analytics') {
    updateChartData()
  }
})

// Note: Auto-save removed to prevent conflicts. Email settings are saved explicitly when needed.

// Watch for employee chart period changes with debouncing
let chartUpdateTimeout: ReturnType<typeof setTimeout> | null = null
watch(employeeChartPeriod, (newPeriod) => {
  console.log(`📊 Chart period changed to ${newPeriod} days`)
  
  // Clear any pending updates
  if (chartUpdateTimeout) {
    clearTimeout(chartUpdateTimeout)
  }
  
  // Debounce chart updates to prevent rapid successive calls
  chartUpdateTimeout = setTimeout(() => {
    updateEmployeeChart()
  }, 100)
})

onMounted(() => {
  checkAuthStatus()
  
  // Skip auth check in development mode
  const isDevelopment = import.meta.env.DEV
  
  if (!isDevelopment && (!isAuthenticated.value || !isAdmin.value)) {
    router.push('/admin/login')
    return
  }
  
  // Set fake user for development
  if (isDevelopment && !user.value) {
    // Temporarily set user for development
    console.log('🔧 Development mode: bypassing auth')
  }
  
  loadDashboardData()
  loadEmployees()
  loadEmailSettings()
  loadVisitorData() // Load real visitor data
  loadEmployeeStats() // Load employee tracking data
  updateChartData() // Initialize charts
  
  // Auto-refresh stats every 30 seconds
  const statsInterval = setInterval(() => {
    if (activeTab.value === 'overview') {
      refreshStats()
    }
  }, 30000)
  
  // Auto-refresh employee data every 60 seconds  
  const employeeInterval = setInterval(() => {
    if (activeTab.value === 'employees') {
      loadEmployeeStats()
    }
  }, 60000)
  
  // Cleanup intervals on unmount
  const cleanup = () => {
    clearInterval(statsInterval)
    clearInterval(employeeInterval)
    
    // Clear chart update timeout
    if (chartUpdateTimeout) {
      clearTimeout(chartUpdateTimeout)
    }
    
    // Destroy employee chart
    if (employeeChart.value) {
      try {
        employeeChart.value.destroy()
        employeeChart.value = null
        console.log('📊 Chart destroyed on cleanup')
      } catch (error) {
        console.warn('Error destroying chart on cleanup:', error)
      }
    }
  }
  
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', cleanup)
  }
})
</script>